import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Json } from '@/types/database.generated';

export interface PunchCardUsage {
  id: string;
  appointment_id: string | null;
  clips_used: number;
  used_at: string;
  session_name: string | null;
  session_date: string | null;
  session_time: string | null;
  appointment_type_name: string | null;
  punch_number: number | null;
  total_punches: number | null;
  remaining_punches_after: number;
}

export interface PunchCardAdjustment {
  id: string;
  type: 'add' | 'deduct';
  amount: number;
  reason: string;
  previous_remaining: number;
  new_remaining: number;
  previous_total: number;
  new_total: number;
  adjusted_type_id: string | null;
  adjusted_type_name: string | null;
  adjusted_type_category: 'appointment' | 'group' | null;
  adjusted_at: string;
}

export interface PunchCard {
  id: string;
  clinic_id: string;
  patient_id: string;
  name: string;
  total_clips: number;
  used_clips: number;
  price_cents: number;
  currency: string;
  purchased_at: string;
  expires_at: string | null;
  invoice_id: string | null;
  invoice_number: string | null;
  status: 'active' | 'used' | 'expired' | 'cancelled';
  appointment_types: unknown[];
  group_types: unknown[];
  punch_card_number: string | null;
  created_at: string;
  updated_at: string;
}

export const patientPunchCardsQueryKey = (
  patientId: string | null | undefined,
  clinicId?: string | null,
) =>
  clinicId
    ? (['punch-cards', 'patient', patientId, clinicId] as const)
    : (['punch-cards', 'patient', patientId] as const);

export function usePatientPunchCards(
  patientId: string | null | undefined,
  clinicId?: string | null,
) {
  return useQuery({
    queryKey: patientPunchCardsQueryKey(patientId, clinicId),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<PunchCard[]> => {
      if (!patientId) return [];

      let cardsQuery = supabase.from('punch_cards').select('*').eq('patient_id', patientId);
      if (clinicId) cardsQuery = cardsQuery.eq('clinic_id', clinicId);
      const { data, error } = await cardsQuery.order('purchased_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as PunchCard[];
    },
    staleTime: 15_000,
  });
}

export const punchCardUsagesQueryKey = (punchCardId: string | null | undefined) =>
  ['punch-card-usages', punchCardId] as const;

export function usePunchCardUsages(punchCardId: string | null | undefined) {
  return useQuery({
    queryKey: punchCardUsagesQueryKey(punchCardId),
    enabled: Boolean(punchCardId),
    queryFn: async (): Promise<PunchCardUsage[]> => {
      if (!punchCardId) return [];

      const { data, error } = await supabase
        .from('punch_card_usages')
        .select('*')
        .eq('punch_card_id', punchCardId)
        .order('used_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as PunchCardUsage[];
    },
    staleTime: 15_000,
  });
}

export const punchCardAdjustmentsQueryKey = (punchCardId: string | null | undefined) =>
  ['punch-card-adjustments', punchCardId] as const;

export function usePunchCardAdjustments(punchCardId: string | null | undefined) {
  return useQuery({
    queryKey: punchCardAdjustmentsQueryKey(punchCardId),
    enabled: Boolean(punchCardId),
    queryFn: async (): Promise<PunchCardAdjustment[]> => {
      if (!punchCardId) return [];

      const { data, error } = await supabase
        .from('punch_card_adjustments')
        .select('*')
        .eq('punch_card_id', punchCardId)
        .order('adjusted_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as PunchCardAdjustment[];
    },
    staleTime: 15_000,
  });
}

export interface CreatePunchCardInput {
  clinic_id: string;
  patient_id: string;
  name: string;
  total_clips: number;
  price_cents: number;
  currency?: string;
  expires_at?: string | null;
  appointment_types?: unknown[];
  group_types?: unknown[];
}

export interface CreatePunchCardWithInvoiceInput {
  clinic_id: string;
  patient_id: string;
  name: string;
  total_clips: number;
  price_cents: number;
  currency?: string;
  expires_at?: string | null;
  appointment_types?: unknown[];
  group_types?: unknown[];
  create_invoice?: boolean;
  vat_rate_percent?: number;
}

export function useCreatePunchCardWithInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePunchCardWithInvoiceInput) => {
      const { data, error } = await supabase.rpc('create_punch_card_with_invoice', {
        p_clinic_id: input.clinic_id,
        p_patient_id: input.patient_id,
        p_name: input.name,
        p_total_clips: input.total_clips,
        p_price_cents: input.price_cents,
        p_currency: input.currency ?? 'DKK',
        p_expires_at: input.expires_at ?? undefined,
        p_appointment_types: (input.appointment_types ?? []) as any,
        p_group_types: (input.group_types ?? []) as any,
        p_create_invoice: input.create_invoice ?? true,
        p_vat_rate_percent: input.vat_rate_percent,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: patientPunchCardsQueryKey(variables.patient_id),
      });
    },
  });
}

export function useCreatePunchCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePunchCardInput) => {
      const { data, error } = await supabase
        .from('punch_cards')
        .insert({
          clinic_id: input.clinic_id,
          patient_id: input.patient_id,
          name: input.name,
          total_clips: input.total_clips,
          price_cents: input.price_cents,
          currency: input.currency ?? 'DKK',
          expires_at: input.expires_at,
          appointment_types: (input.appointment_types ?? []) as Json,
          group_types: (input.group_types ?? []) as Json,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: patientPunchCardsQueryKey(variables.patient_id),
      });
    },
  });
}

export interface UpdatePunchCardInput {
  punch_card_id: string;
  used_clips?: number;
  status?: 'active' | 'used' | 'expired' | 'cancelled';
  invoice_id?: string | null;
  invoice_number?: string | null;
}

export function useUpdatePunchCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdatePunchCardInput) => {
      const { punch_card_id, ...updates } = input;

      const { data, error } = await supabase
        .from('punch_cards')
        .update(updates)
        .eq('id', punch_card_id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: patientPunchCardsQueryKey(data.patient_id),
      });
    },
  });
}

export interface DeletePunchCardInput {
  punch_card_id: string;
  patient_id: string;
}

export function useDeletePunchCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: DeletePunchCardInput) => {
      const { error } = await supabase.from('punch_cards').delete().eq('id', input.punch_card_id);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: patientPunchCardsQueryKey(variables.patient_id),
      });
    },
  });
}

export interface CreateAdjustmentInput {
  punch_card_id: string;
  patient_id: string;
  type: 'add' | 'deduct';
  amount: number;
  reason: string;
  previous_remaining: number;
  new_remaining: number;
  previous_total: number;
  new_total: number;
  adjusted_type_id?: string | null;
  adjusted_type_name?: string | null;
  adjusted_type_category?: 'appointment' | 'group' | null;
}

export function useCreateAdjustment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateAdjustmentInput) => {
      const { patient_id, ...adjustmentData } = input;

      const { data, error } = await supabase
        .from('punch_card_adjustments')
        .insert(adjustmentData)
        .select()
        .single();

      if (error) throw error;

      // Also update the punch card
      const { error: updateError } = await supabase
        .from('punch_cards')
        .update({
          used_clips: input.previous_remaining - input.new_remaining,
          total_clips: input.new_total,
          status: input.new_remaining <= 0 ? 'used' : 'active',
        })
        .eq('id', input.punch_card_id);

      if (updateError) throw updateError;

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: patientPunchCardsQueryKey(variables.patient_id),
      });
      queryClient.invalidateQueries({
        queryKey: punchCardAdjustmentsQueryKey(variables.punch_card_id),
      });
    },
  });
}
